// Whether an access point's RADIUS-placed clients have a path, on its page in
// Operate (#1865): what its newest state report says, read through the
// device's own read (`radius_vlan_path` on `GET /devices/{serial}`), and when
// that report was taken.
//
// **Three answers and a fourth, kept apart in the words.** `found` is a client
// placed on a network the report does not list; `none` is a report that
// strands nobody; `unknown` is a report that cannot say; and an absent field
// is a report that was not read (offline, no report, or no time on it). The
// one that must never be drawn as another is `unknown` as `none`.
//
// **Nothing here sends anything.** The recovery is a person's, by hand over
// the console, and the page says how; there is no button that restarts the
// access point's Wi-Fi, because there is no such act on `/v1`.
import { plural } from "../../dashboard/format";
import type { components } from "../../api/schema";
import { Info, When } from "../../ui";
import { unrecognized } from "../../api/openEnum";

type Path = components["schemas"]["RadiusVlanPath"];

/** The detached restart #1854 recorded as recovering an access point, verbatim. */
export const DETACHED_RESTART = "( (wifi down; sleep 3; wifi up) > /tmp/x.log 2>&1 & )";

/** How many clients the finding strands, in all. */
export function strandedCount(p: Path): number {
  return p.stranded.reduce((n, s) => n + s.stations, 0);
}

/** The finding in one line, for the identity facts. Undefined `p` is a
 * report that was not read, which is said as such and never as `none`. */
export function radiusVlanWords(p: Path | undefined, online: boolean): string {
  if (!p) {
    return online ? "not read: the access point's newest report names no time, or its controller holds none" : "not read while the access point is not online";
  }
  switch (p.state) {
    case "found": {
      const n = strandedCount(p);
      return `${n} ${plural(n, "client")} on a VLAN the access point is not carrying`;
    }
    case "none":
      return "every client placed on a VLAN is on one the access point is carrying";
    case "unknown":
      return "unknown: this access point's reports have not been seen listing the VLANs it carries";
    // Restates ADR-0511 decision 5a — a state this build does not know is shown as received, marked,
    // and never as `unknown` or `none`.
    default:
      return `${unrecognized(p.state)}: this page cannot say what the access point's report found`;
  }
}

export function RadiusVlanFact({ path, online, status }: {
  path: Path | undefined;
  /** `online_state` is `online`, as the page's device read says. */
  online: boolean;
  /** The device read's status: nothing is said of a read still loading or failed. */
  status: string;
}) {
  if (status === "loading") return <span className="muted">…</span>;
  if (status !== "ready") return <span className="muted">—</span>;
  const words = radiusVlanWords(path, online);
  // Restates ADR-0420 decision 1a — the newest report alone, `unknown` never
  // said as `none`, and the report's own time beside every answer.
  return (
    <>
      <span className={!path || path.state !== "found" ? "muted" : undefined}>{path?.state === "found" ? <strong>{words}</strong> : words}</span>
      {path && <> · report of <When value={path.reported_at} /></>}
    </>
  );
}

/** The notice an access point's page carries while its newest report strands
 * a client: where, how many, and the recovery by hand. Nothing for any other
 * answer. */
export function RadiusVlanNotice({ path, mayConsole }: { path: Path | undefined; mayConsole: boolean }) {
  if (!path || path.state !== "found") return null;
  const n = strandedCount(path);
  return (
    <section className="panel notice" aria-label="Clients with no path">
      {/* Restates ADR-0420 decision 1a — a client placed on a VLAN its network's
          list in the newest report does not carry, counted and never named; the
          access point still reads online, and the list is inferred, on one model
          and firmware, to be what the access point has bridged. */}
      <p>
        <strong>{n} {plural(n, "client")} on this access point {n === 1 ? "is" : "are"} on a VLAN it is not carrying.</strong>{" "}
        Its newest report, of <When value={path.reported_at} />, places {n === 1 ? "it" : "them"} on a VLAN that the
        network {n === 1 ? "it is" : "they are"} on does not list, so {n === 1 ? "it" : "they"} most likely {n === 1 ? "gets" : "get"} no
        address. The access point still reads online, and that is not changed by this.
        {/* How far the inference has been checked, behind the ⓘ beside the finding (#1900). */}
        <Info label="About this finding">
          That the list is what the access point carries was worked out by comparing it with the access point&rsquo;s
          own console on one model and firmware, and has not been confirmed anywhere else.
        </Info>
      </p>
      <ul>
        {path.stranded.map((s) => (
          <li key={`${s.iface}/${s.ssid}/${s.vlan}`}>
            {s.ssid} on <code>{s.iface}</code>, VLAN {s.vlan}: {s.stations} {plural(s.stations, "client")}
          </li>
        ))}
      </ul>
      {/* Restates ADR-0420 decision 3a — the recovery is by hand over the
          console, detached so it outlives the session, and nothing is sent
          for it. The command is #1854's, recorded as recovering two units. */}
      <p>
        <strong>To recover it by hand</strong>, {mayConsole ? "open this access point's console" : "platform staff open this access point's console"} and
        restart its Wi-Fi in the background, so that the restart outlives the console session:
      </p>
      <pre aria-label="The detached Wi-Fi restart">{DETACHED_RESTART}</pre>
      <p className="muted">
        Typed as it stands, without the background wrapper, <code>wifi down</code> ends the console session and the rest never
        runs: that has left an access point&rsquo;s radios down for about 90 seconds. <code>wifi reload</code> did not recover it. Every client on the access point is disconnected while its Wi-Fi restarts, for a time not yet measured.
        Nothing is sent to the access point from here.
      </p>
    </section>
  );
}
